import {DOCUMENT} from '@angular/common';
import {Capacitor} from '@capacitor/core';
import {Component, DestroyRef, ElementRef, EventEmitter, HostListener, Injector, Input, OnChanges, AfterViewInit, OnDestroy, Output, SimpleChanges, ViewChild, afterNextRender, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationStart, Router} from '@angular/router';
import {IonicModule, MenuController} from '@ionic/angular';
import {SmModalStackService} from '../sm-confirmation-dialog/sm-modal-stack.service';
import {TranslateModule} from '@ngx-translate/core';
import {filter} from 'rxjs';
import {addOutline, removeOutline, expandOutline, contractOutline, closeOutline, chevronBackOutline, chevronForwardOutline} from 'ionicons/icons';

@Component({
  selector: 'sm-photo-gallery',
  standalone: true,
  imports: [TranslateModule, IonicModule],
  templateUrl: './sm-photo-gallery.component.html',
  styleUrls: ['./sm-photo-gallery.component.scss'],
})
export class SmPhotoGalleryComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  @Input() portalToBody = false;
  private readonly router = inject(Router);
  private readonly menu = inject(MenuController);
  private readonly modalStack = inject(SmModalStackService);
  private readonly destroyRef = inject(DestroyRef);
  @Input() images: string[] = [];
  @Input() loading = false;
  @Input() loadFailed = false;
  @Input() itemName = '';
  @Input() isOpen = false;
  @Output() closed = new EventEmitter<void>();
  @ViewChild('panel') private panel?: ElementRef<HTMLDialogElement>;

  @ViewChild('imageArea') private imageArea?: ElementRef<HTMLElement>;
  @ViewChild('photo') private photo?: ElementRef<HTMLImageElement>;
  readonly icons = {zoomIn: addOutline, zoomOut: removeOutline, expand: expandOutline,
    contract: contractOutline, close: closeOutline, previous: chevronBackOutline, next: chevronForwardOutline};
  controlsVisible = true;
  scale = 1;
  imageAspectRatio: number | null = null;
  photoReady = false;
  private frameReady = false;
  offsetX = 0;
  offsetY = 0;
  fullscreen = false;
  private ownsFullscreen = false;
  private pinchDistance = 0;
  private gestureMoved = false;
  private mousePosition: {x: number; y: number} | null = null;
  index = 0;
  failedImages = new Set<number>();
  private touchStart: {x: number; y: number} | null = null;

  constructor() {
    this.router.events.pipe(filter(event => event instanceof NavigationStart), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.close());
  }

  ngAfterViewInit(): void {
    const dialog = this.panel?.nativeElement;
    if (this.portalToBody && dialog) this.document.body.appendChild(dialog);
    this.syncDialog();
  }
  ngOnDestroy(): void {
    const dialog = this.panel?.nativeElement;
    if (dialog) this.modalStack.closed(dialog);
    this.leaveFullscreen();
    dialog?.close();
    if (this.portalToBody) dialog?.remove();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['images']) {
      this.imageAspectRatio = null;
      this.photoReady = false;
      this.frameReady = false;
      this.resetZoom();
      this.index = 0;
      this.failedImages = new Set<number>();
    }
    this.syncDialog();
  }

  previous(): void { this.resetZoom(); if (this.images.length > 1) { this.photoReady = false; this.index = (this.index - 1 + this.images.length) % this.images.length; } }
  next(): void { this.resetZoom(); if (this.images.length > 1) { this.photoReady = false; this.index = (this.index + 1) % this.images.length; } }
  close(event?: Event): void {
    event?.stopPropagation();
    const dialog = this.panel?.nativeElement;
    if (!dialog?.open) return;
    this.leaveFullscreen();
    this.resetZoom();
    this.modalStack.closed(dialog);
    dialog.close();
    this.closed.emit();
  }
  handleClose(): void {
    if (this.panel?.nativeElement.open) return;
    this.leaveFullscreen();
    this.resetZoom();
    if (this.panel?.nativeElement) this.modalStack.closed(this.panel.nativeElement);
    if (this.isOpen) this.closed.emit();
  }
  handleCancel(event: Event): void {
    event.preventDefault();
    if (this.fullscreen) this.leaveFullscreen(); else this.close();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    const dialog = this.panel?.nativeElement;
    if (!dialog?.open || this.modalStack.top() !== dialog) return;
    if (event.key === 'Tab' || event.key === 'Enter') this.controlsVisible = true;
    if (event.key === 'ArrowLeft') { event.preventDefault(); this.previous(); }
    if (event.key === 'ArrowRight') { event.preventDefault(); this.next(); }
  }

  handleDialogClick(event: MouseEvent): void {
    // The dialog is portaled outside its search/card host. Its clicks must not
    // dismiss that host, including the click that closes this dialog.
    event.stopPropagation();
    const dialog = this.panel?.nativeElement;
    if (this.preparingFrame && !(event.target as Element).closest('button')) { this.close(); return; }
    if (this.fullscreen && this.imageArea?.nativeElement.contains(event.target as Node)) {
      if (!this.gestureMoved) this.controlsVisible = !this.controlsVisible;
      return;
    }
    if (!dialog || event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right ||
      event.clientY < rect.top || event.clientY > rect.bottom) this.close();
  }

  resetZoom(): void {
    this.scale = 1;
    this.offsetX = this.offsetY = 0;
    this.touchStart = this.mousePosition = null;
    this.pinchDistance = 0;
  }

  zoomBy(factor: number): void {
    this.setZoom(this.scale * factor);
  }

  handlePhotoLoad(): void {
    const image = this.photo?.nativeElement;
    this.imageAspectRatio = image?.naturalHeight ? image.naturalWidth / image.naturalHeight : null;
    this.photoReady = true;
    this.frameReady = true;
    this.clampOffsets();
    this.focusAfterRender();
  }

  get preparingPhoto(): boolean {
    return this.loading || (!!this.images[this.index] && !this.failedImages.has(this.index) && !this.photoReady);
  }

  get preparingFrame(): boolean {
    return this.preparingPhoto && !this.frameReady;
  }

  handlePhotoError(): void {
    this.failedImages.add(this.index);
    this.frameReady = true;
  }

  handleWheel(event: WheelEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const area = this.imageArea?.nativeElement;
    if (!area || !this.photo?.nativeElement.naturalWidth || !event.deltaY || this.preparingPhoto || this.failedImages.has(this.index)) return;
    const rect = area.getBoundingClientRect();
    this.setZoom(this.scale * (event.deltaY < 0 ? 1.25 : 0.8),
      event.clientX - rect.left - rect.width / 2,
      event.clientY - rect.top - rect.height / 2);
  }

  private setZoom(value: number, x = 0, y = 0): void {
    const next = Math.max(1, Math.min(5, value));
    const ratio = next / this.scale;
    this.offsetX = x - (x - this.offsetX) * ratio;
    this.offsetY = y - (y - this.offsetY) * ratio;
    this.scale = next;
    this.clampOffsets();
  }

  @HostListener('window:resize')
  clampOffsets(): void {
    const area = this.imageArea?.nativeElement;
    const image = this.photo?.nativeElement;
    if (!area || !image?.naturalWidth) return;
    const fit = Math.min(area.clientWidth / image.naturalWidth, area.clientHeight / image.naturalHeight);
    const maxX = Math.max(0, (image.naturalWidth * fit * this.scale - area.clientWidth) / 2);
    const maxY = Math.max(0, (image.naturalHeight * fit * this.scale - area.clientHeight) / 2);
    this.offsetX = Math.max(-maxX, Math.min(maxX, this.offsetX));
    this.offsetY = Math.max(-maxY, Math.min(maxY, this.offsetY));
  }

  handleTouchStart(event: TouchEvent): void {
    this.gestureMoved = this.gestureMoved && event.touches.length > 1;
    this.beginTouch(event.touches);
  }

  private beginTouch(touches: TouchList): void {
    const first = touches[0];
    this.touchStart = first ? {x: first.clientX, y: first.clientY} : null;
    const second = touches[1];
    this.pinchDistance = second ? Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY) : 0;
    if (second) this.gestureMoved = true;
  }

  handleTouchMove(event: TouchEvent): void {
    if (event.cancelable) event.preventDefault();
    if (event.touches.length > 1) {
      this.pinch(event.touches);
    } else if (this.scale > 1 && this.touchStart) {
      const touch = event.touches[0];
      this.offsetX += touch.clientX - this.touchStart.x;
      this.offsetY += touch.clientY - this.touchStart.y;
      this.gestureMoved = true;
      this.beginTouch(event.touches);
      this.clampOffsets();
    }
  }

  private pinch(touches: TouchList): void {
    const a = touches[0], b = touches[1];
    const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const rect = this.imageArea!.nativeElement.getBoundingClientRect();
    if (this.pinchDistance > 0) {
      this.setZoom(this.scale * distance / this.pinchDistance,
        (a.clientX + b.clientX) / 2 - rect.left - rect.width / 2,
        (a.clientY + b.clientY) / 2 - rect.top - rect.height / 2);
    }
    this.beginTouch(touches);
  }

  handleTouchEnd(event: TouchEvent): void {
    if (event.touches.length) { this.beginTouch(event.touches); return; }
    const touch = event.changedTouches[0];
    const start = this.touchStart;
    this.touchStart = null;
    this.pinchDistance = 0;
    if (!touch || !start || this.gestureMoved || this.scale > 1) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < 48 || Math.abs(dx) <= Math.abs(dy)) return;
    this.gestureMoved = true;
    if (dx < 0) this.next(); else this.previous();
  }

  cancelGesture(): void {
    this.touchStart = this.mousePosition = null;
    this.pinchDistance = 0;
    this.gestureMoved = true;
  }

  handleMouseDown(event: PointerEvent): void {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    this.gestureMoved = false;
    if (this.scale <= 1) return;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.mousePosition = {x: event.clientX, y: event.clientY};
    event.preventDefault();
  }

  handleMouseMove(event: PointerEvent): void {
    if (!this.mousePosition) return;
    if (Math.hypot(event.clientX - this.mousePosition.x, event.clientY - this.mousePosition.y) > 3) this.gestureMoved = true;
    this.offsetX += event.clientX - this.mousePosition.x;
    this.offsetY += event.clientY - this.mousePosition.y;
    this.mousePosition = {x: event.clientX, y: event.clientY};
    this.clampOffsets();
  }

  endMouseGesture(): void { this.mousePosition = null; }

  async toggleFullscreen(): Promise<void> {
    if (this.fullscreen) { this.leaveFullscreen(); return; }
    this.fullscreen = true;
    this.controlsVisible = false;
    this.panel?.nativeElement.focus();
    // Android WebView can hide a modal dialog behind its native fullscreen surface.
    if (Capacitor.isNativePlatform()) return;
    const root = this.document.documentElement;
    if (!this.document.fullscreenElement && root.requestFullscreen) {
      try {
        await root.requestFullscreen();
        this.ownsFullscreen = true;
        if (!this.panel?.nativeElement.open || !this.fullscreen) this.leaveFullscreen();
      } catch { /* The viewport-sized dialog remains available in unsupported WebViews. */ }
    }
  }

  private leaveFullscreen(): void {
    this.fullscreen = false;
    this.controlsVisible = true;
    if (this.ownsFullscreen && this.document.fullscreenElement) void this.document.exitFullscreen().catch(() => undefined);
    this.ownsFullscreen = false;
  }

  @HostListener('document:fullscreenchange')
  handleFullscreenChange(): void {
    if (!this.document.fullscreenElement && this.ownsFullscreen) {
      this.ownsFullscreen = false;
      this.fullscreen = false;
      this.controlsVisible = true;
    }
    this.clampOffsets();
  }

  private syncDialog(): void {
    const dialog = this.panel?.nativeElement;
    if (!dialog) return;
    if (this.isOpen && !dialog.open) {
      void this.menu.close('main-menu');
      dialog.showModal();
      this.modalStack.opened(dialog);
      dialog.focus();
      this.focusAfterRender();
    } else if (!this.isOpen && dialog.open) {
      this.leaveFullscreen();
      this.resetZoom();
      this.modalStack.closed(dialog);
      dialog.close();
    }
  }

  private focusAfterRender(): void {
    afterNextRender(() => {
      const dialog = this.panel?.nativeElement;
      if (dialog?.open && this.modalStack.top() === dialog && !dialog.contains(this.document.activeElement)) {
        dialog.focus({preventScroll: true});
      }
    }, {injector: this.injector});
  }
}
